import React, { useMemo } from 'react';

import { Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { marginService } from '@swyftx/margin';
import {
  MarginInstrument,
  MarginOrderDirection,
  MarginOrderType,
  ProtectionOrderVariant,
} from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { observer } from 'mobx-react-lite';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { useMarginProtectionOrderError } from 'src/lib/margin/hooks/trade';

type Props = {
  lots: string;
  price: string;
  currentPrice: string;
  protectionPrice: number;
  side?: MarginOrderDirection;
  tradeType: MarginOrderType;
  protectionVariant: ProtectionOrderVariant;
  weight?: NumericProps['weight'];
  instrument: MarginInstrument;
};

const EstimatedMarginProfitLoss: React.FC<Props> = observer(
  ({ lots, price, protectionPrice, side, weight, instrument, currentPrice, protectionVariant, tradeType }) => {
    const { getCurrencyRate } = useMarginPrices();
    const { currency } = useMarginAccount();

    const { error } = useMarginProtectionOrderError({
      value: protectionPrice.toString(),
      tradeSide: side || MarginOrderDirection.Long,
      tradeType,
      protectionOrderType: protectionVariant,
      price: currentPrice,
      selectedMarket: instrument,
    });

    const estimatedLoss = useMemo(() => {
      if (!currency || !protectionPrice || error) return Big(0);

      const rate = getCurrencyRate(side === MarginOrderDirection.Short ? 'bid' : 'ask');

      if (!rate) return Big(0);

      const value = marginService.getProfitLossValue({
        lots: Number(lots),
        orderPrice: price,
        contractSize: instrument.contractSize,
        protectionOrderPrice: Big(protectionPrice).times(rate).toString(),
      });

      // Get the inverse price for shorting
      return side === MarginOrderDirection.Short ? value.times(-1) : value;
    }, [currency, error, getCurrencyRate, instrument.contractSize, lots, price, protectionPrice, side]);

    const color = useMemo((): NumericProps['color'] => {
      if (estimatedLoss.eq(0)) return 'placeholder';

      return estimatedLoss.gt(0) ? 'success' : 'error';
    }, [estimatedLoss]);

    return (
      <Numeric color={color} weight={weight}>
        ≈ {estimatedLoss.lte(0) ? '' : '+'}
        {formatCurrency(estimatedLoss.round(2, 0), currency, {
          appendCode: true,
          hideCode: false,
        })}
      </Numeric>
    );
  },
);

EstimatedMarginProfitLoss.displayName = 'EstimatedMarginProfitLoss';

export { EstimatedMarginProfitLoss };
